// src/pages/AdminPanel.jsx

import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Popconfirm,
    message,
    Typography,
    Divider,
    Table,
    Input
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Container, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from '../config';


const { Title, Text } = Typography;
const { Search } = Input;
const baseUrl = config.baseUrl;

const AdminPanel = () => {
    const token = useSelector((state) => state.auth.user?.token);
    const [sessions, setSessions] = useState([]);
    const [filter, setFilter] = useState('');

    // 1) Carga la lista de sesiones
    const fetchSessions = async () => {
        try {
            const { data } = await axios.get(
                `${baseUrl}/api/adminKepler/sessions`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true
                }
            );
            setSessions(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            message.error('No se pudieron cargar las sesiones.');
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    // 2) Forzar logout global
    const handleForceLogoutGlobal = async () => {
        try {
            await axios.post(
                `${baseUrl}/api/adminKepler/force-logout`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true
                }
            );
            message.success('Todas las sesiones han sido cerradas.');
            fetchSessions();
        } catch (err) {
            console.error(err);
            message.error('Error al forzar desconexión global.');
        }
    };

    // 3) Forzar logout de un usuario según su OID
    const handleForceLogoutUser = async (userOid) => {
        try {
            await axios.post(
                `${baseUrl}/api/adminKepler/force-logout/${userOid}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true
                }
            );
            message.success(`Sesión del usuario ${userOid} cerrada.`);
            fetchSessions();
        } catch (err) {
            console.error(err);
            message.error(`Error al cerrar sesión de usuario ${userOid}.`);
        }
    };

    // Columnas de la tabla
    const columns = [
        { title: 'OID', dataIndex: 'oid', key: 'oid', align: 'center' },
        { title: 'Usuario', dataIndex: 'user', key: 'user', align: 'center' },
        { title: 'Login', dataIndex: 'loginUser', key: 'loginUser', align: 'center' },
        { title: 'IP', dataIndex: 'last_ip', key: 'last_ip', align: 'center' },
        { title: 'Último login', dataIndex: 'last_login', key: 'last_login' },
        {
            title: 'Acción',
            key: 'action',
            render: (_, record) => (
                <Button danger onClick={() => handleForceLogoutUser(record.user)}>
                    Cerrar sesión
                </Button>
            )
        }
    ];

    // Filtrar sesiones por loginUser
    const filteredSessions = sessions.filter((s) =>
        s.loginUser?.toLowerCase().includes(filter.trim().toLowerCase())
    );

    return (
        <Container className="py-5">
            <Row>
                {/* Card en la izquierda */}
                <Col xs={12} md={3} className="mb-4">
                    <Card
                        variant="default"
                        style={{
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}
                        className="p-4 h-100"
                    >
                        <div className="text-center">
                            <ExclamationCircleOutlined
                                style={{ fontSize: 48, color: '#faad14' }}
                            />
                            <Title level={4} className="mt-2">
                                Panel de Administración
                            </Title>
                        </div>
                        <Divider />
                        <Text>
                            Con estos controles podrás cerrar sesiones globalmente o por
                            usuario individual.
                        </Text>
                        <div className="d-grid gap-2 mt-4">
                            <Popconfirm
                                title="¿Seguro que deseas cerrar todas las sesiones?"
                                icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                                onConfirm={handleForceLogoutGlobal}
                                okText="Sí, cerrar"
                                cancelText="Cancelar"
                            >
                                <Button type="primary" danger size="large" block>
                                    🔒 Cerrar todas las sesiones
                                </Button>
                            </Popconfirm>
                        </div>
                    </Card>
                </Col>

                {/* Tabla a la derecha */}
                <Col xs={12} md={9}>
                    {/* Input de búsqueda */}
                    <Search
                        placeholder="Buscar por login"
                        allowClear
                        onChange={(e) => setFilter(e.target.value)}
                        style={{ width: 300, marginBottom: 16, marginRight: '16px' }}
                    />

                    <Button type="primary" danger onClick={fetchSessions}>
                        🔒 Refrescar
                    </Button>

                    <div style={{ overflowX: 'auto' }}>
                        <Table
                            rowKey="oid"
                            dataSource={filteredSessions}
                            columns={columns}
                            pagination={{ pageSize: 5 }}
                            scroll={{ x: 'max-content' }}
                        />
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default AdminPanel;
